import Axios, { AxiosResponse } from 'axios';
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    ISampleBundle,
    ISampleBundleCreateResult,
    ISampleBundleDraft,
    ISampleBundleOptions,
    ISampleBundlePrefill,
    ISampleBundleQuote,
    sampleBundleQuoteAtom,
    sampleBundleSubmittingAtom,
    sampleBundlesAtom,
    sampleBundlesLoadingAtom,
    sampleBundlesTotalAtom
} from "./types";

export const SAMPLE_BUNDLE_PAGE_SIZE = 25;

type SampleBundleFunctions = {
    fetchOptions: () => Promise<ISampleBundleOptions | null>;
    fetchPrefill: (projectId: number) => Promise<ISampleBundlePrefill | null>;
    quoteBundle: (draft: ISampleBundleDraft) => Promise<ISampleBundleQuote | null>;
    createBundle: (draft: ISampleBundleDraft) => Promise<ISampleBundleCreateResult | null>;
    fetchBundles: (page: number, append?: boolean) => Promise<void>;
    fetchProjectBundles: (projectId: number) => Promise<ISampleBundle[]>;
    fetchBundle: (bundleId: number) => Promise<ISampleBundle | null>;
};

type UseSampleBundles = [
    ISampleBundle[],
    SampleBundleFunctions
];

// #region toPayload
// Draft -> API payload. Blank optional fields are sent as undefined rather than
// empty strings so the backend's "not provided" and "provided as empty" cases
// stay distinguishable.
const toPayload = (draft: ISampleBundleDraft) => ({
    interior_project_id: draft.interior_project_id ?? undefined,
    client_name: draft.client_name.trim(),
    client_email: draft.client_email.trim() || undefined,
    ship_address_1: draft.ship_address_1.trim(),
    ship_address_2: draft.ship_address_2.trim() || undefined,
    ship_city: draft.ship_city.trim(),
    ship_state: draft.ship_state.trim(),
    ship_zip: draft.ship_zip.trim(),
    ship_country: draft.ship_country.trim() || 'US',
    default_material_id: draft.default_material_id ?? undefined,
    default_scale: draft.default_scale ?? undefined,
    notes: draft.notes?.trim() || undefined,
    items: draft.items.map((item) => ({
        design_id: item.design_id,
        material_id: item.material_id ?? undefined,
        scale: item.scale ?? undefined,
        quantity: item.quantity
    }))
});

// #region useSampleBundles
export const useSampleBundles = (): UseSampleBundles => {
    const [bundles, setBundles] = useAtom(sampleBundlesAtom);
    const setTotal = useSetAtom(sampleBundlesTotalAtom);
    const setLoading = useSetAtom(sampleBundlesLoadingAtom);
    const setQuote = useSetAtom(sampleBundleQuoteAtom);
    const setSubmitting = useSetAtom(sampleBundleSubmittingAtom);

    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const headers = { 'X-Wallmates-Auth': authToken };

    const fetchOptions = async (): Promise<ISampleBundleOptions | null> => {
        const response: AxiosResponse<ISampleBundleOptions> | void = await Axios.get(
            `${environment.designerSampleBundlesUrl}/options`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        return response ? response.data : null;
    };

    const fetchPrefill = async (projectId: number): Promise<ISampleBundlePrefill | null> => {
        const response: AxiosResponse<ISampleBundlePrefill> | void = await Axios.get(
            `${environment.designerSampleBundlesUrl}/prefill?projectId=${projectId}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (!response) {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while loading the designs from that project. Please try again later.'
            });
            return null;
        }
        return response.data;
    };

    // A failed quote is not surfaced as a snackbar — the create page shows the
    // reason inline in the summary panel, which is where the designer is looking.
    const quoteBundle = async (draft: ISampleBundleDraft): Promise<ISampleBundleQuote | null> => {
        try {
            const response = await Axios.post<ISampleBundleQuote>(
                `${environment.designerSampleBundlesUrl}/quote`,
                toPayload(draft),
                { headers }
            );
            setQuote(response.data);
            return response.data;
        } catch (err: any) {
            setQuote(null);
            if (err?.response?.status === 401) errorCatcher(err, loginFunctions.logout);
            throw err;
        }
    };

    const createBundle = async (draft: ISampleBundleDraft): Promise<ISampleBundleCreateResult | null> => {
        setSubmitting(true);
        try {
            const response = await Axios.post<ISampleBundleCreateResult>(
                environment.designerSampleBundlesUrl,
                toPayload(draft),
                { headers }
            );
            setSnackbar({
                show: true,
                snackbarLevel: 'success',
                text: `Sample bundle sent — order #${response.data.order_number}.`
            });
            return response.data;
        } catch (err: any) {
            if (err?.response?.status === 401) errorCatcher(err, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message || 'There was an error while sending the sample bundle. Please try again later.'
            });
            return null;
        } finally {
            setSubmitting(false);
        }
    };

    // #region fetchProjectBundles
    // The bundles belonging to one interior project. Returned directly rather
    // than through the history atom so the project page owns its own list and
    // cannot fight the /sample-bundles page for that state.
    const fetchProjectBundles = async (projectId: number): Promise<ISampleBundle[]> => {
        try {
            const response = await Axios.get<{ bundles: ISampleBundle[]; total: number }>(
                `${environment.designerSampleBundlesUrl}?page=1&limit=${SAMPLE_BUNDLE_PAGE_SIZE}&projectId=${projectId}`,
                { headers }
            );
            return response.data?.bundles ?? [];
        } catch {
            // Non-fatal: the project page just omits the sample-bundle section.
            return [];
        }
    };

    const fetchBundles = async (page: number, append: boolean = false): Promise<void> => {
        setLoading(true);
        const response: AxiosResponse<{ bundles: ISampleBundle[]; total: number }> | void = await Axios.get(
            `${environment.designerSampleBundlesUrl}?page=${page}&limit=${SAMPLE_BUNDLE_PAGE_SIZE}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (response) {
            setBundles((previous: ISampleBundle[]) => append ? [...previous, ...response.data.bundles] : response.data.bundles);
            setTotal(response.data.total);
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while loading your sample bundles. Please try again later.'
            });
        }
        setLoading(false);
    };

    const fetchBundle = async (bundleId: number): Promise<ISampleBundle | null> => {
        const response: AxiosResponse<ISampleBundle> | void = await Axios.get(
            `${environment.designerSampleBundlesUrl}/${bundleId}`,
            { headers }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        return response ? response.data : null;
    };

    return [
        bundles,
        { fetchOptions, fetchPrefill, quoteBundle, createBundle, fetchBundles, fetchProjectBundles, fetchBundle }
    ];
};
